import { useCallback } from 'react';
import { useAlert } from 'react-alert';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { useActions } from '../../../../hooks/useActions';
import { useApiResponseFail } from '../../../../hooks/useApiResponseFail';
import { useProcessing } from '../../../../hooks/useProcessing';
import {
  ProductSubscriptionOverviewPageParams,
  RoutePath,
} from '../../../../models/enums/RoutePath';
import { IValidationErrorResponse } from '../../../../models/ValidationErrorResponse';
import { IWarningRatification } from '../../../../models/interfaces/WarningRatification';
import { organizationContractsSelector } from '../../../../store/redux-slices/contracts/selectors';
import { updateModalState } from '../../../../store/redux-slices/modals';
import { organizationSelector } from '../../../../store/redux-slices/organizations/selectors';
import {
  SUBSCRIPTION_RENEWAL_SUGGESTION_LOADING_PROCESSING,
  SUBSCRIPTION_RENEW_PROCESSING,
} from '../../../../store/redux-slices/processes/constants';
import { ProductSubscriptionRenewPageParamsType } from '../../../../models/types/RoutePath';
import { useNavigateSearch } from '../../../../hooks/useNavigateSearch';
import { IOrganizationSubscription } from '../../../../models/interfaces/Subscription/OrganizationSubscription';
import { AddContractModalFormDetails } from '../../../../styledComponents/Modals/AddContractModal/AddContractModalForm/addContractModalForm.models';
import { ADD_CONTRACT_MODAL_NAME } from '../../../../styledComponents/Modals/AddContractModal/addContractModal.const';
import { renewTLSubscription } from '../../../../store/saga-slices/subscriptions/travelLicense';
import { tlSubscriptionRenewalSuggestionSelector } from '../../../../store/redux-slices/subscriptions/travelLicense/selectors';
import { RenewTLSubscriptionModel } from '../../../../models/types/TravelLicense/RenewTLSubscriptionModel';
import { IRenewTLSubscriptionPayload } from '../../../../store/saga-slices/subscriptions/travelLicense/models';
import { TLSubscriptionDefaultValueArray } from '../../../../styledComponents/Forms/TravelLicense/TLSubscriptionPricingForm/tlSubscriptionPricingForm.const';
import { validateTLRenewSubscriptionForm } from './tlSubscriptionRenewPageForm.validation';
import { useFormErrorsActions } from '../../../../styledComponents/FormErrorProvider/FormErrorProvider.hooks';

export const useTLSubscriptionRenewForm = () => {
  const { t } = useTranslation();

  const navigate = useNavigateSearch();

  const { organizationId, subscriptionId, productType } =
    useParams<ProductSubscriptionRenewPageParamsType>();

  const alert = useAlert();

  const { validateErrors } = useFormErrorsActions();

  const UpdateModalState = useActions(updateModalState);

  const RenewSubscription = useActions(renewTLSubscription);

  const isSubscriptionUpdating = useProcessing(SUBSCRIPTION_RENEW_PROCESSING);

  const isSuggestionDataLoading = useProcessing(
    SUBSCRIPTION_RENEWAL_SUGGESTION_LOADING_PROCESSING,
  );

  const { handleResponse } = useApiResponseFail();

  const organization = useSelector(organizationSelector);
  const organizationContracts = useSelector(organizationContractsSelector);

  const subscriptionRenewalSuggestion = useSelector(
    tlSubscriptionRenewalSuggestionSelector,
  );

  const renewSubscriptionHandler = useCallback(
    (
      values: RenewTLSubscriptionModel,
      ratifications: IWarningRatification[] = [],
    ) => {
      const extendedValues: RenewTLSubscriptionModel = {
        ...values,
        ratifications,
      };

      const payload: IRenewTLSubscriptionPayload = {
        data: extendedValues,
        organizationId,
        subscriptionId,
        success: (data: IOrganizationSubscription) => {
          navigate(
            RoutePath.ClientOverviewProductSubscriptionOverviewPageFullPath,
            {
              params: {
                [ProductSubscriptionOverviewPageParams.OrganizationId]:
                  organizationId,
                [ProductSubscriptionOverviewPageParams.SubscriptionId]: data.id,
                [ProductSubscriptionOverviewPageParams.SubscriptionType]:
                  productType,
              },
            },
          );
          alert.success(t('alerts.subscription-renew-success'));
        },
        error: (err: IValidationErrorResponse) => {
          handleResponse({
            err,
            values,
            ratifications,
            alertErrorMessage: t('alerts.subscription-renew-fail'),
          });
        },
      };
      RenewSubscription(payload);
    },
    [
      RenewSubscription,
      alert,
      handleResponse,
      navigate,
      organizationId,
      productType,
      subscriptionId,
      t,
    ],
  );

  const handleSubmit = (values: RenewTLSubscriptionModel) => {
    renewSubscriptionHandler(values);
  };

  const onAddNewContractHandler = () => {
    const details: AddContractModalFormDetails = {
      organizationId,
    };
    UpdateModalState([ADD_CONTRACT_MODAL_NAME, details]);
  };

  const initState = {
    pricingTiers: TLSubscriptionDefaultValueArray,
    ...subscriptionRenewalSuggestion,
  };

  const validateForm = (data: RenewTLSubscriptionModel) =>
    validateTLRenewSubscriptionForm(data, validateErrors(data));

  return {
    handleSubmit,
    organization,
    isSubscriptionUpdating,
    organizationContracts,
    canChangeContract:
      subscriptionRenewalSuggestion?.metadata?.canChangeContract,
    isSuggestionDataLoading,
    initState,
    UpdateModalState,
    renewSubscriptionHandler,
    productType,
    onAddNewContractHandler,
    validateForm,
  };
};
